import styled from '@emotion/styled';
import {
  palette,
  status,
  spacing,
  radius,
  shadow,
  typography,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';

// Styled layer for a rendered Ask answer. Every value is a token; no raw hex
// (design_system.md:75-78). NOTHING HERE IS AURORA — the aurora gradient is the
// AI mark and it already lives on the chat header, the FAB and the handover
// card. An answer body wearing it a second time inside a surface that already
// carries it is decoration, which §10 explicitly rules out.

/* ─────────────────────  THE NEWLINE FIX  ─────────────────────
 *
 * THE SINGLE HIGHEST-VALUE LINE IN THIS FILE IS `white-space: pre-wrap`.
 *
 * The server emits \n-joined answers in at least four places — ask-facts.js:247
 * (renderPackages), :256 (renderOccupants), :269 (renderLocation) and
 * ask-semantic.js:207 (fallbackAnswer) — and all four reach the client verbatim
 * (ask-query.js:306, iq.js:809). They were being rendered inside a bare <span>
 * whose ABubble parent had no white-space rule, so every newline collapsed to a
 * single space and a forty-parcel list arrived as one unbroken run-on
 * paragraph: "- 14:22 4B — DHL for Maria Santos- 14:31 …".
 *
 * QBubble (the user's own question) has carried `white-space: pre-wrap` since it
 * was written (iq-chat.styled.tsx:262). Only the AI side was missing it.
 *
 * IT GOES ON THE TEXT ELEMENT, NOT ON ABubble. ABubble is
 * `display:flex; flex-direction:column` (iq-chat.styled.tsx:277-279) and also
 * hosts the error string and the "Checking the log…" placeholder; putting
 * pre-wrap on the flex container would make every incidental newline in that
 * JSX render as blank space in the bubble.
 */
export const AnswerText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
`;

/** A short label above a table — what this list IS, before the reader parses it. */
export const AnswerLabel = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.half};
`;

/* ─────────────────────────  THE TABLE  ─────────────────────────
 *
 * Wide content scrolls inside its own wrapper and NEVER clips: design_system.md
 * §8.4 names `overflow-x: hidden` on a wide table as the bug this repo already
 * fixed once. Under `md` the whole thing reflows to stacked label:value cards,
 * which is the same rule's other half — a parcel table has seven columns and a
 * concierge reads it on a phone at the desk.
 */
export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.inkSoft};

  th,
  td {
    text-align: left;
    padding: ${spacing.xs} ${spacing.sm};
    vertical-align: top;
    white-space: nowrap;
  }

  thead th {
    font-size: ${typography.size.xxs}px;
    font-weight: ${typography.weight.semibold};
    letter-spacing: ${typography.tracking.caps};
    text-transform: uppercase;
    color: ${palette.slate};
    background: ${palette.surfaceMuted};
    border-bottom: 1px solid ${palette.line};
  }

  tbody tr + tr td {
    border-top: 1px solid ${palette.line};
  }

  tbody tr:hover td {
    background: ${palette.tealTintSoft};
  }

  /* Reflow to cards under md (design_system.md §8.4). The header row is dropped
   * and each cell grows its own label from data-label, so there is ONE DOM for
   * both layouts — a second, phone-only render of the same rows would be a
   * second place for the two to disagree. */
  ${down('md')} {
    display: block;

    thead {
      display: none;
    }

    tbody,
    tr,
    td {
      display: block;
    }

    tbody tr {
      padding: ${spacing.xs} 0;
    }

    tbody tr + tr {
      border-top: 1px solid ${palette.line};
    }

    tbody tr + tr td {
      border-top: none;
    }

    td {
      display: grid;
      grid-template-columns: minmax(96px, 34%) 1fr;
      gap: ${spacing.xs};
      white-space: normal;
      padding: ${spacing.half} ${spacing.sm};
    }

    td::before {
      content: attr(data-label);
      font-size: ${typography.size.xxs}px;
      font-weight: ${typography.weight.semibold};
      letter-spacing: ${typography.tracking.caps};
      text-transform: uppercase;
      color: ${palette.slateLight};
    }
  }
`;

/** Monospace for a time or a shelf code — the same token SourceChip and the raw
 *  log's Time already use, so codes line up across every IQ surface. */
export const Mono = styled.span`
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

/** A value the row does not carry. Rendered as an explicit dash rather than an
 *  empty cell so "there is no carrier on this parcel" is visibly different from
 *  "this table failed to render". */
export const Absent = styled.span`
  color: ${palette.slateLight};
`;

/* A caveat that must be read WITH the answer, not after it. Neutral tint: this
 * is a scoping note, not a warning, and spending the alert colour on it would
 * make every list look like an incident. */
export const Caveat = styled.p`
  margin: 0;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.xs};
  background: ${status.neutral.bg};
  color: ${status.neutral.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
`;
